import { notifyError } from "@/lib/notify-error";
/* eslint-disable complexity */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { Outlet, createFileRoute, Navigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  useTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { appTableFeatures } from "@/lib/table-features";
import { useConvex, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Download,
  Filter,
  Folder,
  FolderOpen,
  GripVertical,
  Loader2,
  PencilLine,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v4";

import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import {
  DocumentTypeLabel,
  isRevisionProcessingFailed,
  revisionTypeLabelProps,
} from "@/components/document-type-label";
import { AppLink } from "@/components/app-link";
import {
  LinkedRevisionJobsButton,
  type LinkedRevisionJob,
} from "@/components/linked-revision-jobs";
import { ProjectDocumentViewer } from "@/components/project-document-viewer";
import { RowAction, RowActionsMenu } from "@/components/row-actions-menu";
import { RevisionBadge, RoleBadge } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RevisionSheetIndex } from "@/features/sheet-index/revision-sheet-index";
import { DocumentSubRow } from "@/components/projects/document-sub-row";
import { RetryProcessingAction, type RevisionRetry, useRevisionRetry } from "@/components/projects/revision-retry";
import type { GlobalProcessingState } from "@/lib/processing-states";
import {
  buildDocumentDownloadFilename,
  downloadUrlAsFile,
} from "@/lib/document-download";
import {
  DOCUMENT_DRAG_MIME,
  getDocumentIdsForDrag,
  isDocumentDrag,
  isFileDrag,
  parseDocumentDragData,
  partitionDroppedFiles,
} from "@/lib/project-document-drag";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useInlineEdit } from "@/lib/use-inline-edit";
import { cn, formatDate, REVEAL_ON_ROW_HOVER, revisionHasDocumentType, type DocumentTypeLabelValue } from "@/lib/utils";
import {
  DocumentActionsMenu,
  ProjectAccessDialog,
  ProjectDocumentAddMenu,
  ProjectDocumentsTable,
  type ProjectDocumentListItem,
  useProject,
} from "@/components/projects";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

const ROOT_FOLDER_VALUE = "__root__";
const NO_PROJECT_MANAGER_VALUE = "__no_project_manager__";
const OVERVIEW_DOCUMENT_PAGE_SIZE = 5;
const REVISION_DOCUMENT_SELECT_PAGE_SIZE = 10;
const PILOT_LINKED_DELETE_REASON =
  "Linked to existing SpecPilot, RevPilot, or TakeoffPilot items";
const JOB_ATTACHED_DELETE_REASON = "Can't delete because it is linked to a job.";

type DocumentTypeFilter = Extract<DocumentTypeLabelValue, "schedule" | "plan_set" | "mixed">;

const DOCUMENT_TYPE_FILTER_OPTIONS: { value: DocumentTypeFilter; label: string }[] = [
  { value: "schedule", label: "Schedule" },
  { value: "plan_set", label: "Plan Set" },
  { value: "mixed", label: "Mixed" },
];

function toggleSetKey(current: Set<string>, key: string) {
  const next = new Set(current);
  if (!next.delete(key)) next.add(key);
  return next;
}

/** Filter values a revision matches; a mixed revision also matches the types of its pages. */
function getDocumentTypeFilterValues(
  revision: Parameters<typeof revisionHasDocumentType>[0] = {}
): DocumentTypeFilter[] {
  return DOCUMENT_TYPE_FILTER_OPTIONS.map((option) => option.value).filter((value) =>
    value === "mixed"
      ? revision.effectiveDocumentType === "mixed"
      : revisionHasDocumentType(revision, value)
  );
}

interface FolderDocumentRef {
  folderPath?: string | null;
}

interface FolderTrailItem {
  label: string;
  path: string;
}

interface ProjectOverviewFieldProps {
  label: string;
  value?: string | null;
  detail?: string;
}

interface ProjectStatProps {
  label: string;
  description: string;
  value?: number;
  onClick: () => void;
}

interface RevisionDeleteEligibility {
  _id: Id<"projectDocumentRevisions">;
  revisionName: string;
  canDelete?: boolean;
  deleteBlockedReason?: string | null;
}

interface DocumentDeleteEligibility {
  _id: Id<"projectDocuments">;
  name: string;
  canDelete?: boolean;
  deleteBlockedReason?: string | null;
}

interface DeleteEligibilityRequest {
  documentId: Id<"projectDocuments">;
  revisionId?: Id<"projectDocumentRevisions">;
}

interface ActionLogSession {
  _id: Id<"actionLogSessions">;
  pilot: "SpecPilot" | "RevPilot" | "TakeoffPilot" | "CollabPilot" | "DocPilot";
  targetKind:
    | "specPilotSubmittal"
    | "revPilotComparison"
    | "takeoffPilotJob"
    | "projectDocument"
    | "projectDocumentRevision";
  targetId: string;
  targetName: string;
  userName: string;
  startedAt: number;
}

interface ActionLogCardProps {
  sessions?: ActionLogSession[];
  isPending: boolean;
  isError: boolean;
}

const ProjectDetailsSchema = z.object({
  projectName: z.string().min(1, "Project name is required"),
  projectNumber: z.string().optional(),
  projectManager: z.string().optional(),
  projectAddress: z.string().optional(),
});

type ProjectDetailsFormValues = z.infer<typeof ProjectDetailsSchema>;

function countDocumentsInFolder(
  documents: FolderDocumentRef[],
  folderPath: string
): number {
  return documents.filter((document) => (document.folderPath ?? "") === folderPath).length;
}

function buildFolderTrail(currentFolderPath: string): FolderTrailItem[] {
  const trail: FolderTrailItem[] = [{ label: "Project Documents", path: "" }];
  if (!currentFolderPath) {
    return trail;
  }

  let accumulatedPath = "";
  for (const segment of currentFolderPath.split("/")) {
    accumulatedPath = accumulatedPath ? `${accumulatedPath}/${segment}` : segment;
    trail.push({ label: segment, path: accumulatedPath });
  }

  return trail;
}

function formatFolderOptionPrefix(depth: number): string {
  if (depth <= 0) {
    return "";
  }

  return `${"|  ".repeat(Math.max(depth - 1, 0))}|- `;
}

function formatRevisionDeleteBlockedReason(reason?: string | null): string | null {
  if (!reason) {
    return null;
  }

  return reason === PILOT_LINKED_DELETE_REASON ? JOB_ATTACHED_DELETE_REASON : reason;
}

function getRevisionDeleteDisabledReason(
  revision: RevisionDeleteEligibility,
  revisionCount: number
): string | null {
  if (revisionCount <= 1) {
    return "Cannot delete the only revision. Delete the document instead.";
  }

  if (revision.canDelete !== true) {
    return (
      formatRevisionDeleteBlockedReason(revision.deleteBlockedReason) ??
      JOB_ATTACHED_DELETE_REASON
    );
  }

  return null;
}

function getDocumentDeleteDisabledReason(document: DocumentDeleteEligibility): string | null {
  if (document.canDelete !== true) {
    return (
      formatRevisionDeleteBlockedReason(document.deleteBlockedReason) ??
      JOB_ATTACHED_DELETE_REASON
    );
  }

  return null;
}

function getLinkedRevisionJobs(revision: { linkedJobs?: unknown }): LinkedRevisionJob[] {
  return Array.isArray(revision.linkedJobs)
    ? (revision.linkedJobs as LinkedRevisionJob[])
    : [];
}

function SpinnerIcon({ className }: { className?: string }) {
  return <Loader2 className={cn(className, "animate-spin")} />;
}

interface DocumentDeleteDropdownItemProps {
  document: DocumentDeleteEligibility;
  isDeleting: boolean;
  onRequestDelete: (documentId: Id<"projectDocuments">) => void;
}

function DocumentDeleteDropdownItem({
  document,
  isDeleting,
  onRequestDelete,
}: DocumentDeleteDropdownItemProps) {
  const disabledReason = getDocumentDeleteDisabledReason(document);
  if (isDeleting) {
    return (
      <RowAction icon={SpinnerIcon} destructive disabled onSelect={() => undefined}>
        Delete
      </RowAction>
    );
  }

  if (disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="menuitem"
            aria-disabled="true"
            title={disabledReason}
            className="relative flex cursor-not-allowed select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive opacity-50 outline-hidden"
          >
            <Trash2 className="size-4" />
            Delete
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{disabledReason}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <RowAction
      icon={Trash2}
      destructive
      onSelect={(event) => {
        event.preventDefault();
        onRequestDelete(document._id);
      }}
    >
      Delete
    </RowAction>
  );
}

interface RevisionDeleteDropdownItemProps {
  revision: RevisionDeleteEligibility;
  revisionCount: number;
  label: string;
  isDeleting: boolean;
  onRequestDelete: (revisionId: Id<"projectDocumentRevisions">) => void;
}

function RevisionDeleteDropdownItem({
  revision,
  revisionCount,
  label,
  isDeleting,
  onRequestDelete,
}: RevisionDeleteDropdownItemProps) {
  const disabledReason = getRevisionDeleteDisabledReason(revision, revisionCount);
  if (isDeleting) {
    return (
      <RowAction icon={SpinnerIcon} destructive disabled onSelect={() => undefined}>
        {label}
      </RowAction>
    );
  }

  if (disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="menuitem"
            aria-disabled="true"
            title={disabledReason}
            className="relative flex cursor-not-allowed select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive opacity-50 outline-hidden"
          >
            <Trash2 className="size-4" />
            {label}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{disabledReason}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <RowAction
      icon={Trash2}
      destructive
      onSelect={(event) => {
        event.preventDefault();
        onRequestDelete(revision._id);
      }}
    >
      {label}
    </RowAction>
  );
}

interface ProjectDocumentNameCellProps {
  document: {
    _id: Id<"projectDocuments">;
    name: string;
    revisionCount: number;
  };
  revisionLevel?: number;
  startEditing?: boolean;
  onStartEditingHandled?: () => void;
  onOpen: (documentId: Id<"projectDocuments">) => void;
  onUpdateName: (documentId: Id<"projectDocuments">, name: string) => Promise<void>;
}

function ProjectDocumentNameCell({
  document,
  revisionLevel = 0,
  startEditing = false,
  onStartEditingHandled,
  onOpen,
  onUpdateName,
}: ProjectDocumentNameCellProps) {
  const canEdit = useCanEdit();
  const inlineEdit = useInlineEdit(document.name, (name) => onUpdateName(document._id, name));
  const { setIsEditing } = inlineEdit;
  useOnEditAccessLost(() => {
    setIsEditing(false);
    inlineEdit.setValue(document.name);
  });

  useEffect(() => {
    if (!startEditing) {
      return;
    }

    if (canEdit) setIsEditing(true);
    onStartEditingHandled?.();
  }, [canEdit, onStartEditingHandled, setIsEditing, startEditing]);

  if (canEdit && inlineEdit.isEditing) {
    return (
      <Input
        ref={inlineEdit.inputRef}
        value={inlineEdit.value}
        onChange={(event) => inlineEdit.setValue(event.currentTarget.value)}
        onBlur={() => void inlineEdit.handleSave()}
        onKeyDown={inlineEdit.handleKeyDown}
        className="h-8 w-full max-w-xs"
        aria-label={`Edit ${document.name}`}
      />
    );
  }

  return (
    <div className={cn("flex min-w-0 items-start gap-2", revisionLevel > 0 && "ps-7")}>
      <button
        type="button"
        onClick={() => onOpen(document._id)}
        className="min-w-0 flex-1 text-left"
        aria-label={`Open ${document.name}`}
      >
        <div className="min-w-0 space-y-1">
          <p className="truncate font-medium text-foreground">{document.name}</p>
          <p className="text-xs text-muted-foreground">
            {document.revisionCount} revision{document.revisionCount === 1 ? "" : "s"}
          </p>
        </div>
      </button>
      {canEdit && (
        <IconButton
          label={`Rename ${document.name}`}
          icon={PencilLine}
          size="xs"
          className={REVEAL_ON_ROW_HOVER}
          onClick={() => inlineEdit.setIsEditing(true)}
        />
      )}
    </div>
  );
}

function formatActionLogTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const time = date
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(" ", "")
    .toLowerCase();
  const day = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  return `${time} ${day}`;
}

function ActionLogTargetLink({ session }: { session: ActionLogSession }) {
  const className = "font-medium text-primary hover:underline";
  const title = `${session.pilot} · ${session.targetName}`;

  switch (session.targetKind) {
    case "specPilotSubmittal":
      return (
        <AppLink
          to="/specPilot/$jobId"
          params={{ jobId: session.targetId }}
          title={title}
          className={className}
        >
          {session.targetName}
        </AppLink>
      );
    case "revPilotComparison":
      return (
        <AppLink
          to="/revPilot/$comparisonId"
          params={{ comparisonId: session.targetId }}
          title={title}
          className={className}
        >
          {session.targetName}
        </AppLink>
      );
    case "takeoffPilotJob":
      return (
        <AppLink
          to="/takeoffPilot/$jobId"
          params={{ jobId: session.targetId }}
          title={title}
          className={className}
        >
          {session.targetName}
        </AppLink>
      );
    case "projectDocument":
      if (session.pilot === "CollabPilot") {
        return (
          <AppLink
            to="/revCollab/documents/$documentId"
            params={{ documentId: session.targetId }}
            title={title}
            className={className}
          >
            {session.targetName}
          </AppLink>
        );
      }

      return (
        <AppLink
          to="/project/documents/$documentId"
          params={{ documentId: session.targetId }}
          title={title}
          className={className}
        >
          {session.targetName}
        </AppLink>
      );
    case "projectDocumentRevision":
      return (
        <AppLink
          to="/revCollab/revisions/$revisionId"
          params={{ revisionId: session.targetId }}
          title={title}
          className={className}
        >
          {session.targetName}
        </AppLink>
      );
  }
}

function ActionLogCard({ sessions, isPending, isError }: ActionLogCardProps) {
  const rows = sessions ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Action Log</CardTitle>
        <CardDescription>Recent pilot editing sessions for this project.</CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading action log...
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">Unable to load action log right now.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pilot sessions have been logged yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pilot</TableHead>
                  <TableHead>Pilot Job</TableHead>
                  <TableHead>Name of Editor</TableHead>
                  <TableHead>When Edit Occurred</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((session) => (
                  <TableRow key={session._id}>
                    <TableCell>{session.pilot}</TableCell>
                    <TableCell>
                      <ActionLogTargetLink session={session} />
                    </TableCell>
                    <TableCell>{session.userName}</TableCell>
                    <TableCell>{formatActionLogTimestamp(session.startedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectOverviewField({ label, value, detail }: ProjectOverviewFieldProps) {
  return (
    <div className="py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-foreground">{value?.trim() || "-"}</dd>
      {detail ? <dd className="mt-1 text-xs text-muted-foreground">{detail}</dd> : null}
    </div>
  );
}

function ProjectStat({ label, description, value, onClick }: ProjectStatProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-3 rounded-lg border-2 px-4 py-3 text-left transition-colors hover:border-primary hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground group-hover:text-primary">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
      </span>
      <span className="shrink-0 text-2xl font-semibold tabular-nums">{value ?? "-"}</span>
    </button>
  );
}

export const Route = createFileRoute("/project")({
  component: ProjectRoute,
});

export function ProjectRoute() {
  const { projectId } = useProject();
  const appNavigate = useAppNavigate();
  const convex = useConvex();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isProjectDocumentsIndex = pathname === "/project/documents";
  const isProjectDocumentDetail = pathname.startsWith("/project/documents/");
  const shouldLoadProjectPageData = Boolean(projectId && !isProjectDocumentDetail);
  const shouldLoadOverviewStats = shouldLoadProjectPageData && !isProjectDocumentsIndex;

  const typedProjectId = projectId as Id<"projects">;

  const { data: project } = useQuery(
    convexQuery(
      api.projects.getById,
      shouldLoadProjectPageData ? { projectId: typedProjectId } : "skip"
    )
  );
  const { data: viewer } = useQuery(
    convexQuery(api.auth.viewer, shouldLoadProjectPageData ? {} : "skip")
  );
  const { data: documents } = useQuery(
    convexQuery(
      api.projectDocuments.listByProject,
      shouldLoadProjectPageData ? { projectId: typedProjectId } : "skip"
    )
  );
  const { data: specPilotJobs } = useQuery(
    convexQuery(
      api.specPilot.submittals.listAll,
      shouldLoadOverviewStats ? { projectId: typedProjectId } : "skip"
    )
  );
  const { data: takeoffPilotJobs } = useQuery(
    convexQuery(api.takeoffPilot.jobs.listAll, shouldLoadOverviewStats ? {} : "skip")
  );
  const { data: revPilotJobs } = useQuery(
    convexQuery(
      api.scheduleComparisons.listWithStats,
      shouldLoadOverviewStats ? { projectId: typedProjectId } : "skip"
    )
  );
  const { data: folders } = useQuery(
    convexQuery(
      api.projectDocumentFolders.listByProject,
      shouldLoadProjectPageData ? { projectId: typedProjectId } : "skip"
    )
  );
  const {
    data: members,
    isPending: isMembersPending,
    isError: isMembersError,
    error: membersError,
  } = useQuery(
    convexQuery(
      api.projects.listCurrentMembers,
      shouldLoadProjectPageData ? { projectId: typedProjectId } : "skip"
    )
  );
  const canManageProjectDetails = viewer?.orgRole === "owner" || viewer?.orgRole === "admin";
  // Reviewers are read-only; don't offer writes the server refuses.
  const canEditWork = useCanEdit();
  const { retryRevision, isRetrying: isRetryingRevision } = useRevisionRetry();
  const { data: orgMembers } = useQuery(
    convexQuery(
      api.auth.listOrganizationMembersMinimal,
      shouldLoadProjectPageData && canManageProjectDetails ? {} : "skip"
    )
  );
  const {
    data: actionLogSessions,
    isPending: isActionLogPending,
    isError: isActionLogError,
  } = useQuery(
    convexQuery(
      api.actionLog.listByProject,
      shouldLoadProjectPageData ? { projectId: typedProjectId, limit: 20 } : "skip"
    )
  );

  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);
  const bulkUpload = useMutation(api.projectDocuments.bulkUpload);
  const uploadRevision = useMutation(api.projectDocuments.uploadRevision);
  const updateProjectDetails = useMutation(api.projects.updateDetails);
  const createFolder = useMutation(api.projectDocumentFolders.createFolder);
  const moveDocument = useMutation(api.projectDocuments.moveToFolder);
  const renameDocument = useMutation(api.projectDocuments.renameDocument);
  const removeDocument = useMutation(api.projectDocuments.removeDocument);
  const removeDocuments = useMutation(api.projectDocuments.removeDocuments);
  const removeRevision = useMutation(api.projectDocuments.removeRevision);

  const activeTab = isProjectDocumentsIndex ? "documents" : "overview";
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState<Id<"projectDocuments"> | null>(null);
  const [selectedDocumentRevisionId, setSelectedDocumentRevisionId] = useState<
    Id<"projectDocumentRevisions"> | null
  >(null);
  const [selectedDocumentPage, setSelectedDocumentPage] = useState<number>();
  const [openSheetIndexRevisionIds, setOpenSheetIndexRevisionIds] = useState<Set<string>>(
    () => new Set()
  );
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [openRevisionDocumentIds, setOpenRevisionDocumentIds] = useState<Set<string>>(() => new Set());
  const [moveDocumentIds, setMoveDocumentIds] = useState<Id<"projectDocuments">[]>([]);
  const [moveDestinationPath, setMoveDestinationPath] = useState(ROOT_FOLDER_VALUE);
  const [isMovingDocument, setIsMovingDocument] = useState(false);
  /** Folder path being hovered as a drop target (`""` = root). */
  const [activeDropFolderPath, setActiveDropFolderPath] = useState<string | null>(null);
  /** True while OS files are dragged over the documents library panel. */
  const [isFileDropActive, setIsFileDropActive] = useState(false);
  const [bulkDeleteDocumentIds, setBulkDeleteDocumentIds] = useState<Id<"projectDocuments">[]>([]);
  const [isBulkDeletingDocuments, setIsBulkDeletingDocuments] = useState(false);
  const [isProjectDetailsDialogOpen, setIsProjectDetailsDialogOpen] = useState(false);
  const [accessProject, setAccessProject] = useState<{
    _id: Id<"projects">;
    name: string;
    projectManagerUserId?: Id<"users">;
  } | null>(null);
  const [revisionUploadDocumentId, setRevisionUploadDocumentId] = useState<Id<"projectDocuments"> | null>(null);
  const [isRevisionUploadDialogOpen, setIsRevisionUploadDialogOpen] = useState(false);
  const [revisionDocumentOptionCount, setRevisionDocumentOptionCount] = useState(REVISION_DOCUMENT_SELECT_PAGE_SIZE);
  const [isUploadingRevision, setIsUploadingRevision] = useState(false);
  const [downloadingRevisionId, setDownloadingRevisionId] = useState<Id<"projectDocumentRevisions"> | null>(
    null
  );
  const [documentSorting, setDocumentSorting] = useState<SortingState>([
    { id: "updatedAt", desc: true },
  ]);
  const [documentColumnFilters, setDocumentColumnFilters] = useState<ColumnFiltersState>([]);
  const [deleteDocumentId, setDeleteDocumentId] = useState<Id<"projectDocuments"> | null>(null);
  const [deleteRevisionId, setDeleteRevisionId] = useState<Id<"projectDocumentRevisions"> | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [isDeletingRevision, setIsDeletingRevision] = useState(false);
  const [inlineRenameDocumentId, setInlineRenameDocumentId] = useState<Id<"projectDocuments"> | null>(null);
  // A member demoted mid-session loses any write flow that was already open.
  useOnEditAccessLost(() => {
    setIsCreateFolderDialogOpen(false);
    setMoveDocumentIds([]);
    setBulkDeleteDocumentIds([]);
    setIsRevisionUploadDialogOpen(false);
    setDeleteDocumentId(null);
    setDeleteRevisionId(null);
    setInlineRenameDocumentId(null);
    setSelectedDocumentIds(new Set());
  });
  const [deleteEligibilityRequest, setDeleteEligibilityRequest] =
    useState<DeleteEligibilityRequest | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const revisionFileInputRef = useRef<HTMLInputElement | null>(null);
  const bulkUploadFolderPathRef = useRef<string | null>(null);
  const isDraggingDocumentsRef = useRef(false);
  const projectDetailsForm = useForm<ProjectDetailsFormValues>({
    resolver: zodResolver(ProjectDetailsSchema as never),
    defaultValues: {
      projectName: "",
      projectNumber: "",
      projectManager: NO_PROJECT_MANAGER_VALUE,
      projectAddress: "",
    },
  });
  const {
    control: projectDetailsControl,
    formState: { isSubmitting: isSavingProjectDetails },
    handleSubmit: handleProjectDetailsSubmit,
    register: registerProjectDetails,
    reset: resetProjectDetailsForm,
  } = projectDetailsForm;

  const { data: selectedDocumentDetails } = useQuery(
    convexQuery(
      api.projectDocuments.getDocumentWithRevisions,
      selectedDocumentId && shouldLoadProjectPageData
        ? { projectId: typedProjectId, documentId: selectedDocumentId }
        : "skip"
    )
  );

  const { data: deleteEligibility } = useQuery(
    convexQuery(
      api.projectDocuments.getDeletionEligibility,
      deleteEligibilityRequest && shouldLoadProjectPageData
        ? deleteEligibilityRequest.revisionId
          ? {
              documentId: deleteEligibilityRequest.documentId,
              revisionId: deleteEligibilityRequest.revisionId,
            }
          : { documentId: deleteEligibilityRequest.documentId }
        : "skip"
    )
  );

  const { data: bulkDeleteEligibility, isError: isBulkDeleteEligibilityError } = useQuery(
    convexQuery(
      api.projectDocuments.getBulkDeletionEligibility,
      bulkDeleteDocumentIds.length > 0 && shouldLoadProjectPageData
        ? { projectId: typedProjectId, documentIds: bulkDeleteDocumentIds }
        : "skip"
    )
  );

  const allDocuments = documents ?? [];
  const allFolders = folders ?? [];
  const memberList = members ?? [];
  const projectTakeoffPilotJobCount = takeoffPilotJobs
    ? takeoffPilotJobs.filter((job) => String(job.projectId) === String(typedProjectId)).length
    : undefined;
  const organizationMembers = orgMembers ?? [];
  const projectManager = [...memberList, ...organizationMembers].find(
    (member) => member._id === project?.projectManagerUserId
  );
  const alphabetizedDocuments = useMemo(
    () =>
      allDocuments.slice().sort(
        (a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
          String(a._id).localeCompare(String(b._id))
      ),
    [allDocuments]
  );
  const overviewDocuments = alphabetizedDocuments.slice(0, OVERVIEW_DOCUMENT_PAGE_SIZE);
  const visibleFolders = useMemo(
    () => allFolders.filter((folder) => folder.parentPath === currentFolderPath),
    [allFolders, currentFolderPath]
  );
  const documentsInCurrentFolder = useMemo(
    () => allDocuments.filter((document) => (document.folderPath ?? "") === currentFolderPath),
    [allDocuments, currentFolderPath]
  );
  const revisionUploadDocumentOptions = useMemo(
    () =>
      documentsInCurrentFolder
        .slice()
        .sort(
          (a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
            String(a._id).localeCompare(String(b._id))
        ),
    [documentsInCurrentFolder]
  );
  const visibleRevisionUploadDocumentOptions = revisionUploadDocumentOptions.slice(
    0,
    revisionDocumentOptionCount
  );
  const documentColumns = useMemo<ColumnDef<typeof appTableFeatures, (typeof documentsInCurrentFolder)[number]>[]>(
    () => [
      {
        accessorKey: "name",
        sortFn: "alphanumeric",
      },
      {
        accessorKey: "updatedAt",
        sortFn: "basic",
      },
      {
        id: "documentType",
        accessorFn: (document) => getDocumentTypeFilterValues(document.latestRevision ?? undefined),
        filterFn: "arrIncludesSome",
        enableSorting: false,
      },
    ],
    []
  );
  const documentsTable = useTable({
    features: appTableFeatures,
    data: documentsInCurrentFolder,
    columns: documentColumns,
    state: {
      sorting: documentSorting,
      columnFilters: documentColumnFilters,
    },
    onSortingChange: setDocumentSorting,
    onColumnFiltersChange: setDocumentColumnFilters,
  });
  const visibleDocuments = documentsTable.getRowModel().rows.map((row) => row.original);
  const nameColumn = documentsTable.getColumn("name");
  const updatedColumn = documentsTable.getColumn("updatedAt");
  const documentTypeColumn = documentsTable.getColumn("documentType");
  const selectedDocumentTypeFilters =
    (documentTypeColumn?.getFilterValue() as DocumentTypeFilter[] | undefined) ?? [];

  const applyDocumentDeleteEligibility = (document: DocumentDeleteEligibility) => {
    if (deleteEligibilityRequest?.documentId !== document._id) {
      return document;
    }

    if (!deleteEligibility) {
      return {
        ...document,
        canDelete: false,
        deleteBlockedReason: "Checking whether this document can be deleted...",
      };
    }

    return {
      ...document,
      canDelete: deleteEligibility.canDeleteDocument,
      deleteBlockedReason: deleteEligibility.documentDeleteBlockedReason,
    };
  };

  const applyRevisionDeleteEligibility = (revision: RevisionDeleteEligibility) => {
    if (deleteEligibilityRequest?.revisionId !== revision._id) {
      return revision;
    }

    if (!deleteEligibility) {
      return {
        ...revision,
        canDelete: false,
        deleteBlockedReason: "Checking whether this revision can be deleted...",
      };
    }

    return {
      ...revision,
      canDelete: deleteEligibility.canDeleteSelectedRevision,
      deleteBlockedReason: deleteEligibility.selectedRevisionDeleteBlockedReason,
    };
  };

  const revisionRowsByDocumentId = useMemo(
    () =>
      new Map(
        visibleDocuments.map((document) => {
          const sortedRevisions = (document.revisions ?? [])
            .slice()
            .sort(
              (a, b) =>
                b.revisionNumber - a.revisionNumber ||
                String(b._id).localeCompare(String(a._id))
            );
          return [
            String(document._id),
            {
              sortedRevisions,
              latestRevision: sortedRevisions[0] ?? document.latestRevision ?? null,
              historyRevisions: sortedRevisions.slice(1),
            },
          ];
        })
      ),
    [visibleDocuments]
  );
  const folderTrail = useMemo(() => buildFolderTrail(currentFolderPath), [currentFolderPath]);
  const moveTargetDocuments = useMemo(
    () =>
      moveDocumentIds
        .map((documentId) => allDocuments.find((document) => document._id === documentId) ?? null)
        .filter((document): document is NonNullable<typeof document> => document !== null),
    [allDocuments, moveDocumentIds]
  );
  const bulkDeleteTargetDocuments = useMemo(
    () =>
      bulkDeleteDocumentIds
        .map((documentId) => allDocuments.find((document) => document._id === documentId) ?? null)
        .filter((document): document is NonNullable<typeof document> => document !== null),
    [allDocuments, bulkDeleteDocumentIds]
  );
  const deleteTargetDocument = useMemo(
    () => allDocuments.find((document) => document._id === deleteDocumentId) ?? null,
    [allDocuments, deleteDocumentId]
  );
  const deleteTargetRevision = useMemo(
    () =>
      allDocuments
        .flatMap((document) => document.revisions ?? [])
        .find((revision) => revision._id === deleteRevisionId) ?? null,
    [allDocuments, deleteRevisionId]
  );
  const areAllVisibleDocumentsSelected =
    visibleDocuments.length > 0 &&
    visibleDocuments.every((document) => selectedDocumentIds.has(String(document._id)));
  const hasSomeVisibleDocumentsSelected =
    visibleDocuments.some((document) => selectedDocumentIds.has(String(document._id))) &&
    !areAllVisibleDocumentsSelected;
  const selectedVisibleDocumentIds = useMemo(
    () =>
      visibleDocuments
        .filter((document) => selectedDocumentIds.has(String(document._id)))
        .map((document) => document._id),
    [selectedDocumentIds, visibleDocuments]
  );
  const blockedBulkDeleteDocuments =
    bulkDeleteEligibility?.documents.filter((document) => !document.canDelete) ?? [];
  const isCheckingBulkDeleteEligibility =
    bulkDeleteDocumentIds.length > 0 &&
    bulkDeleteEligibility === undefined &&
    !isBulkDeleteEligibilityError;

  const openDocumentPreview = (
    documentId: Id<"projectDocuments">,
    revisionId: Id<"projectDocumentRevisions"> | null = null,
    pageNumber?: number
  ) => {
    setSelectedDocumentRevisionId(revisionId);
    setSelectedDocumentPage(pageNumber);
    setSelectedDocumentId(documentId);
  };

  const closeDocumentPreview = () => {
    setSelectedDocumentId(null);
    setSelectedDocumentRevisionId(null);
    setSelectedDocumentPage(undefined);
  };

  /** Menu props to retry a revision whose processing failed, when the user can edit. */
  const getRevisionRetry = (
    revision: Parameters<typeof isRevisionProcessingFailed>[0] & { _id: Id<"projectDocumentRevisions"> }
  ): RevisionRetry | null =>
    canEditWork && isRevisionProcessingFailed(revision)
      ? {
          isRetrying: isRetryingRevision(revision._id),
          onRetry: () => void retryRevision(revision._id),
        }
      : null;

  const renderSheetIndexToggle = (revision: {
    _id: Id<"projectDocumentRevisions">;
    pageIndexingStatus?: string;
    pageCount?: number;
  }) => {
    if (!revision.pageIndexingStatus) return null;
    const isOpen = openSheetIndexRevisionIds.has(String(revision._id));
    return (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="-ms-2"
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setOpenSheetIndexRevisionIds((current) => toggleSetKey(current, String(revision._id)));
        }}
      >
        {isOpen ? <ChevronDown /> : <ChevronRight />}
        {revision.pageCount ? `${revision.pageCount} Sheets` : "Sheets"}
      </Button>
    );
  };

  const renderSheetIndexRow = (
    documentId: Id<"projectDocuments">,
    revision: { _id: Id<"projectDocumentRevisions">; pageIndexingStatus?: GlobalProcessingState }
  ) =>
    openSheetIndexRevisionIds.has(String(revision._id)) ? (
      <RevisionSheetIndex
        key={`${revision._id}-sheets`}
        revisionId={revision._id}
        pageIndexingStatus={revision.pageIndexingStatus}
        canEdit={canEditWork}
        onOpenPage={(pageNumber) => openDocumentPreview(documentId, revision._id, pageNumber)}
      />
    ) : null;

  const handleToggleRevisionHistory = (documentId: Id<"projectDocuments">) => {
    setOpenRevisionDocumentIds((current) => toggleSetKey(current, String(documentId)));
  };

  const renderSortIcon = (sortDirection: false | "asc" | "desc") => {
    if (!sortDirection) {
      return <ArrowUpDown className="size-3.5 text-muted-foreground" />;
    }

    return sortDirection === "asc" ? (
      <ArrowUp className="size-3.5 text-primary" />
    ) : (
      <ArrowDown className="size-3.5 text-primary" />
    );
  };

  const handleDocumentTypeFilterChange = (value: DocumentTypeFilter, checked: boolean) => {
    const currentFilters = new Set(selectedDocumentTypeFilters);
    if (checked) {
      currentFilters.add(value);
    } else {
      currentFilters.delete(value);
    }

    const nextFilters = Array.from(currentFilters);
    documentTypeColumn?.setFilterValue(nextFilters.length > 0 ? nextFilters : undefined);
  };

  useEffect(() => {
    setCurrentFolderPath("");
  }, [projectId]);

  useEffect(() => {
    setSelectedDocumentIds(new Set());
  }, [currentFolderPath, documentColumnFilters]);

  useEffect(() => {
    if (isProjectDetailsDialogOpen) {
      return;
    }
    resetProjectDetailsForm({
      projectName: project?.name ?? "",
      projectNumber: project?.projectNumber ?? "",
      projectAddress: project?.projectAddress ?? "",
      projectManager: project?.projectManagerUserId ?? NO_PROJECT_MANAGER_VALUE,
    });
  }, [
    isProjectDetailsDialogOpen,
    project?.name,
    project?.projectNumber,
    project?.projectAddress,
    project?.projectManagerUserId,
    resetProjectDetailsForm,
  ]);

  if (!projectId) {
    return <Navigate to="/" />;
  }

  if (isProjectDocumentDetail) {
    return <Outlet />;
  }

  const handleBulkUploadClick = () => {
    bulkUploadFolderPathRef.current = currentFolderPath;
    fileInputRef.current?.click();
  };

  const handleOverviewCreateFolder = () => {
    setCurrentFolderPath("");
    setIsCreateFolderDialogOpen(true);
  };

  const handleOverviewBulkUploadClick = () => {
    bulkUploadFolderPathRef.current = "";
    setCurrentFolderPath("");
    fileInputRef.current?.click();
  };

  const uploadFileToR2 = async (file: File) => {
    const { key, url } = await generateR2UploadUrl({
      prefix: "project-documents/source-files",
      filename: file.name,
    });
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Failed to upload ${file.name}`);
    }

    return {
      originalFilename: file.name,
      fileStorageKey: key,
      mimeType: file.type || undefined,
      fileSizeBytes: file.size,
    };
  };

  const handleBulkUpload = async (
    files: FileList | File[] | null,
    folderPathOverride?: string | null
  ) => {
    if (!files || files.length === 0) return;

    setIsBulkUploading(true);
    try {
      const uploadPayload = await Promise.all(Array.from(files).map(uploadFileToR2));

      const folderPath =
        folderPathOverride !== undefined
          ? folderPathOverride
          : (bulkUploadFolderPathRef.current ?? currentFolderPath);
      const result = await bulkUpload({
        projectId: typedProjectId,
        folderPath: folderPath || undefined,
        uploads: uploadPayload,
      });

      const conflicts = result.results.filter((row) => Boolean(row.conflict));
      if (conflicts.length > 0) {
        toast.warning(
          `${result.createdRevisions} revision(s) uploaded and processing, ${conflicts.length} file(s) skipped due to duplicate revision numbers.`
        );
      } else {
        toast.success(`Uploaded ${result.createdRevisions} revision(s) and started processing`);
      }
    } catch (error) {
      console.error(error);
      notifyError(error, "Document upload failed");
    } finally {
      setIsBulkUploading(false);
      bulkUploadFolderPathRef.current = null;
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRevisionUploadClick = (documentId: Id<"projectDocuments">) => {
    setRevisionUploadDocumentId(documentId);
    revisionFileInputRef.current?.click();
  };

  const handleDownloadRevision = async ({
    documentName,
    revisionId,
    revisionNumber,
    originalFilename,
  }: {
    documentName: string;
    revisionId: Id<"projectDocumentRevisions">;
    revisionNumber: number;
    originalFilename: string;
  }) => {
    if (downloadingRevisionId) {
      return;
    }

    setDownloadingRevisionId(revisionId);
    try {
      const downloadUrl = await convex.query(api.projectDocuments.getRevisionDownloadUrl, {
        revisionId,
      });
      if (!downloadUrl) {
        throw new Error("Download URL unavailable");
      }
      await downloadUrlAsFile(
        downloadUrl,
        buildDocumentDownloadFilename(documentName, originalFilename, revisionNumber)
      );
      toast.success("Download started");
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to download document");
    } finally {
      setDownloadingRevisionId(null);
    }
  };

  const handleDownloadDocument = async (document: ProjectDocumentListItem) => {
    const revision =
      revisionRowsByDocumentId.get(String(document._id))?.latestRevision ?? null;
    if (!revision) {
      toast.error("This document has no revisions to download");
      return;
    }
    await handleDownloadRevision({
      documentName: document.name,
      revisionId: revision._id,
      revisionNumber: revision.revisionNumber,
      originalFilename: revision.originalFilename,
    });
  };

  const handleOpenRevisionUploadDialog = () => {
    setRevisionDocumentOptionCount(REVISION_DOCUMENT_SELECT_PAGE_SIZE);
    setRevisionUploadDocumentId(null);
    setIsRevisionUploadDialogOpen(true);
  };

  const handleSelectRevisionFileFromDialog = () => {
    if (!revisionUploadDocumentId) {
      toast.error("Select the document this revision is for");
      return;
    }

    revisionFileInputRef.current?.click();
  };

  const handleRevisionUpload = async (files: FileList | null) => {
    const file = files?.[0] ?? null;
    if (!file || !revisionUploadDocumentId) return;

    setIsUploadingRevision(true);
    try {
      const uploadPayload = await uploadFileToR2(file);
      await uploadRevision({
        documentId: revisionUploadDocumentId,
        upload: uploadPayload,
      });
      toast.success("Revision uploaded and processing started");
      setIsRevisionUploadDialogOpen(false);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to upload revision");
    } finally {
      setIsUploadingRevision(false);
      setRevisionUploadDocumentId(null);
      if (revisionFileInputRef.current) {
        revisionFileInputRef.current.value = "";
      }
    }
  };

  const handleCreateFolder = async () => {
    const nextFolderName = newFolderName.trim();
    if (!nextFolderName) {
      return;
    }

    setIsCreatingFolder(true);
    try {
      await createFolder({
        projectId: typedProjectId,
        name: nextFolderName,
        parentPath: currentFolderPath || undefined,
      });
      toast.success("Folder created");
      setNewFolderName("");
      setIsCreateFolderDialogOpen(false);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to create folder");
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleSaveProjectDetails = async (values: ProjectDetailsFormValues) => {
    if (!canManageProjectDetails) {
      toast.error("Only organization admins can update project details");
      return;
    }

    const nextName = values.projectName.trim();
    if (!nextName) {
      toast.error("Project name is required");
      return;
    }

    try {
      await updateProjectDetails({
        projectId: typedProjectId,
        name: nextName,
        projectNumber: values.projectNumber?.trim() ?? "",
        projectAddress: values.projectAddress?.trim() ?? "",
        projectManagerUserId: values.projectManager && values.projectManager !== NO_PROJECT_MANAGER_VALUE
          ? (values.projectManager as Id<"users">)
          : null,
      });
      toast.success("Project details updated");
      setIsProjectDetailsDialogOpen(false);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to update project details");
    }
  };

  const handleUpdateDocumentName = async (documentId: Id<"projectDocuments">, name: string) => {
    try {
      await renameDocument({ documentId, name });
      toast.success("Name updated");
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to update name");
      throw error;
    }
  };

  const handleDeleteDocument = async (documentId: Id<"projectDocuments"> | null) => {
    if (!documentId) {
      return;
    }

    setIsDeletingDocument(true);
    try {
      await removeDocument({ documentId });
      toast.success("Document deleted");
      setSelectedDocumentIds((current) => {
        const next = new Set(current);
        next.delete(String(documentId));
        return next;
      });
      setOpenRevisionDocumentIds((current) => {
        const next = new Set(current);
        next.delete(String(documentId));
        return next;
      });
      if (selectedDocumentId === documentId) {
        closeDocumentPreview();
      }
      setDeleteDocumentId(null);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to delete document");
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const handleDeleteRevision = async (revisionId: Id<"projectDocumentRevisions"> | null) => {
    if (!revisionId) {
      return;
    }

    setIsDeletingRevision(true);
    try {
      await removeRevision({ revisionId });
      toast.success("Revision deleted");
      setDeleteRevisionId(null);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to delete revision");
    } finally {
      setIsDeletingRevision(false);
    }
  };

  const toggleDocumentSelection = (documentId: Id<"projectDocuments">, checked: boolean) => {
    setSelectedDocumentIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(String(documentId));
      } else {
        next.delete(String(documentId));
      }
      return next;
    });
  };

  const handleToggleAllVisibleDocuments = (checked: boolean) => {
    setSelectedDocumentIds((current) => {
      const next = new Set(current);
      for (const document of visibleDocuments) {
        if (checked) {
          next.add(String(document._id));
        } else {
          next.delete(String(document._id));
        }
      }
      return next;
    });
  };

  const moveDocumentsToFolder = async (
    documentIds: Id<"projectDocuments">[],
    destinationFolderPath: string | undefined
  ) => {
    if (documentIds.length === 0) {
      return;
    }

    const normalizedDestination = destinationFolderPath ?? "";
    const documentsById = new Map(allDocuments.map((document) => [document._id, document]));
    const idsToMove = documentIds.filter((documentId) => {
      const document = documentsById.get(documentId);
      return document !== undefined && (document.folderPath ?? "") !== normalizedDestination;
    });

    if (idsToMove.length === 0) {
      toast.info(
        documentIds.length === 1
          ? "Document is already in this folder"
          : "Documents are already in this folder"
      );
      return;
    }

    setIsMovingDocument(true);
    try {
      const originalFolderPathByDocumentId = new Map(
        idsToMove.map((documentId) => [
          documentId,
          documentsById.get(documentId)?.folderPath ?? undefined,
        ])
      );
      const moveResults = await Promise.allSettled(
        idsToMove.map((documentId) =>
          moveDocument({
            documentId,
            folderPath: destinationFolderPath,
          })
        )
      );
      const succeededDocumentIds: Id<"projectDocuments">[] = [];
      const failedDocumentIds: Id<"projectDocuments">[] = [];

      moveResults.forEach((result, index) => {
        const documentId = idsToMove[index];
        if (!documentId) {
          return;
        }

        if (result.status === "fulfilled") {
          succeededDocumentIds.push(documentId);
          return;
        }

        failedDocumentIds.push(documentId);
      });

      let rollbackFailed = false;
      if (failedDocumentIds.length > 0 && succeededDocumentIds.length > 0) {
        const rollbackResults = await Promise.allSettled(
          succeededDocumentIds.map((documentId) =>
            moveDocument({
              documentId,
              folderPath: originalFolderPathByDocumentId.get(documentId),
            })
          )
        );
        rollbackFailed = rollbackResults.some((result) => result.status === "rejected");
      }

      if (failedDocumentIds.length === 0) {
        toast.success(idsToMove.length === 1 ? "Document moved" : "Documents moved");
        setMoveDocumentIds([]);
        setSelectedDocumentIds(new Set());
        return;
      }

      if (rollbackFailed) {
        toast.error(
          "Some document moves failed and automatic rollback was incomplete. Review the folder assignments before retrying."
        );
      } else {
        toast.error(
          failedDocumentIds.length === 1
            ? "1 document failed to move. Successful moves were rolled back."
            : `${failedDocumentIds.length} documents failed to move. Successful moves were rolled back.`
        );
      }

      setMoveDocumentIds(idsToMove);
      setSelectedDocumentIds(new Set(idsToMove.map((documentId) => String(documentId))));
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to move document");
    } finally {
      setIsMovingDocument(false);
    }
  };

  const handleMoveDocument = async () => {
    const destinationFolderPath =
      moveDestinationPath === ROOT_FOLDER_VALUE ? undefined : moveDestinationPath;
    await moveDocumentsToFolder(moveDocumentIds, destinationFolderPath);
  };

  const handleDocumentDragStart = (
    event: DragEvent<HTMLButtonElement>,
    documentId: Id<"projectDocuments">
  ) => {
    if (isMovingDocument || isBulkUploading) {
      event.preventDefault();
      return;
    }

    const idsToDrag = getDocumentIdsForDrag(
      documentId,
      selectedDocumentIds,
      selectedVisibleDocumentIds
    );
    isDraggingDocumentsRef.current = true;
    event.dataTransfer.setData(DOCUMENT_DRAG_MIME, JSON.stringify(idsToDrag));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDocumentDragEnd = () => {
    isDraggingDocumentsRef.current = false;
    setActiveDropFolderPath(null);
    setIsFileDropActive(false);
  };

  const isActiveDocumentDrag = (dataTransfer: DataTransfer) =>
    isDraggingDocumentsRef.current || isDocumentDrag(dataTransfer);

  const handleFolderDragOver = (event: DragEvent, folderPath: string) => {
    const documentDrag = isActiveDocumentDrag(event.dataTransfer);
    const fileDrag = isFileDrag(event.dataTransfer);
    if (!documentDrag && !fileDrag) {
      return;
    }
    event.preventDefault();
    if (isMovingDocument || isBulkUploading) {
      return;
    }
    event.dataTransfer.dropEffect = documentDrag ? "move" : "copy";
    setActiveDropFolderPath(folderPath);
    if (fileDrag) {
      setIsFileDropActive(true);
    }
  };

  const handleFolderDragLeave = (event: DragEvent, folderPath: string) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setActiveDropFolderPath((current) => (current === folderPath ? null : current));
  };

  const handleFolderDrop = (event: DragEvent, folderPath: string) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveDropFolderPath(null);
    setIsFileDropActive(false);
    isDraggingDocumentsRef.current = false;

    if (isMovingDocument || isBulkUploading) {
      return;
    }

    const documentIds = parseDocumentDragData(event.dataTransfer);
    if (documentIds) {
      void moveDocumentsToFolder(documentIds, folderPath || undefined);
      return;
    }

    if (event.dataTransfer.files.length > 0) {
      const { uploadableFiles, skippedDirectoryCount } = partitionDroppedFiles(
        Array.from(event.dataTransfer.files)
      );
      if (skippedDirectoryCount > 0) {
        toast.warning(
          skippedDirectoryCount === 1
            ? "1 folder skipped — drop files only."
            : `${skippedDirectoryCount} folders skipped — drop files only.`
        );
      }
      if (uploadableFiles.length === 0) {
        toast.error("No valid files to upload");
        return;
      }
      void handleBulkUpload(uploadableFiles, folderPath);
    }
  };

  const handleLibraryDragOver = (event: DragEvent) => {
    if (!isFileDrag(event.dataTransfer)) {
      return;
    }
    event.preventDefault();
    if (isMovingDocument || isBulkUploading) {
      return;
    }
    event.dataTransfer.dropEffect = "copy";
    setIsFileDropActive(true);
  };

  const handleLibraryDragLeave = (event: DragEvent) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setIsFileDropActive(false);
    setActiveDropFolderPath(null);
  };

  const handleLibraryDrop = (event: DragEvent) => {
    if (!isFileDrag(event.dataTransfer) && event.dataTransfer.files.length === 0) {
      return;
    }
    event.preventDefault();
    setIsFileDropActive(false);
    setActiveDropFolderPath(null);

    if (isMovingDocument || isBulkUploading) {
      return;
    }

    if (event.dataTransfer.files.length > 0) {
      const { uploadableFiles, skippedDirectoryCount } = partitionDroppedFiles(
        Array.from(event.dataTransfer.files)
      );
      if (skippedDirectoryCount > 0) {
        toast.warning(
          skippedDirectoryCount === 1
            ? "1 folder skipped — drop files only."
            : `${skippedDirectoryCount} folders skipped — drop files only.`
        );
      }
      if (uploadableFiles.length === 0) {
        toast.error("No valid files to upload");
        return;
      }
      void handleBulkUpload(uploadableFiles, currentFolderPath);
    }
  };

  const handleBulkDeleteDocuments = async () => {
    if (bulkDeleteDocumentIds.length === 0 || bulkDeleteEligibility?.canDeleteAll !== true) {
      return;
    }

    setIsBulkDeletingDocuments(true);
    try {
      const deletedDocumentIds = new Set(bulkDeleteDocumentIds.map(String));
      await removeDocuments({
        projectId: typedProjectId,
        documentIds: bulkDeleteDocumentIds,
      });
      toast.success(
        bulkDeleteDocumentIds.length === 1 ? "Document deleted" : "Documents deleted"
      );
      setSelectedDocumentIds((current) => {
        const next = new Set(current);
        for (const documentId of deletedDocumentIds) {
          next.delete(documentId);
        }
        return next;
      });
      setOpenRevisionDocumentIds((current) => {
        const next = new Set(current);
        for (const documentId of deletedDocumentIds) {
          next.delete(documentId);
        }
        return next;
      });
      if (selectedDocumentId && deletedDocumentIds.has(String(selectedDocumentId))) {
        closeDocumentPreview();
      }
      setBulkDeleteDocumentIds([]);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to delete documents");
    } finally {
      setIsBulkDeletingDocuments(false);
    }
  };

  const handleOpenMoveDialog = (documentIds: Id<"projectDocuments">[], folderPath?: string | null) => {
    setMoveDocumentIds(documentIds);
    setMoveDestinationPath(folderPath ? folderPath : ROOT_FOLDER_VALUE);
  };

  const handleOpenFullView = (
    documentId = selectedDocumentId,
    revisionId = selectedDocumentRevisionId,
    documentName = selectedDocumentDetails?.document.name
  ) => {
    if (!documentId) return;

    const params = { documentId };
    const title = documentName ?? selectedDocumentDetails?.document.name ?? "Project Document";

    appNavigate({
      to: "/project/documents/$documentId",
      params,
      search: revisionId ? { revisionId: String(revisionId) } : undefined,
      title,
    });
  };

  const handleOpenRevCollab = (revisionId: Id<"projectDocumentRevisions">) => {
    appNavigate({
      to: "/revCollab/revisions/$revisionId",
      params: { revisionId },
      title: selectedDocumentDetails?.document.name
        ? `CollabPilot · ${selectedDocumentDetails.document.name}`
        : "CollabPilot Session",
    });
  };

  if (project === undefined || documents === undefined || folders === undefined) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail={isProjectDocumentsIndex} />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <PageHeader title="Project not found" trail={[{ label: "Projects", href: "/" }]} />
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This project does not exist or you do not have access.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* The TopNav project switcher already names the project, so the overview has no trail. */}
        <PageHeader
          title={isProjectDocumentsIndex ? "Project Documents" : project.name}
          trail={isProjectDocumentsIndex ? [{ label: project.name, href: "/project" }] : undefined}
          actions={
            isProjectDocumentsIndex ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appNavigate({ to: "/project/documents", title: "Documents" })}
              >
                <FolderOpen />
                Documents
              </Button>
            )
          }
        />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => void handleBulkUpload(event.currentTarget.files)}
        />
        <input
          ref={revisionFileInputRef}
          type="file"
          className="hidden"
          onChange={(event) => void handleRevisionUpload(event.currentTarget.files)}
        />

        <Dialog open={isRevisionUploadDialogOpen} onOpenChange={setIsRevisionUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload revision</DialogTitle>
              <DialogDescription>
                Select a document in the current folder, then upload the new revision file.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <label htmlFor="revision-document-select" className="text-sm font-medium">
                Document
              </label>
              <Select
                value={revisionUploadDocumentId ?? undefined}
                onValueChange={(value) => setRevisionUploadDocumentId(value as Id<"projectDocuments">)}
                disabled={revisionUploadDocumentOptions.length === 0 || isUploadingRevision}
              >
                <SelectTrigger id="revision-document-select">
                  <SelectValue
                    placeholder={
                      revisionUploadDocumentOptions.length === 0
                        ? "No documents in this folder"
                        : "Select a document"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {visibleRevisionUploadDocumentOptions.map((document) => (
                    <SelectItem key={document._id} value={document._id}>
                      {document.name}
                    </SelectItem>
                  ))}
                  {revisionDocumentOptionCount < revisionUploadDocumentOptions.length ? (
                    <div className="p-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full justify-center"
                        onClick={(event) => {
                          event.preventDefault();
                          setRevisionDocumentOptionCount((count) => count + REVISION_DOCUMENT_SELECT_PAGE_SIZE);
                        }}
                      >
                        Show 10 more
                      </Button>
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Showing documents scoped to {currentFolderPath ? `/${currentFolderPath}` : "Project Documents"}.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsRevisionUploadDialogOpen(false)}
                disabled={isUploadingRevision}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSelectRevisionFileFromDialog}
                disabled={!revisionUploadDocumentId || isUploadingRevision}
              >
                {isUploadingRevision ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                Upload
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs value={activeTab}>
          {isProjectDocumentsIndex ? null : (
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle>Project Overview</CardTitle>
                        <CardDescription>
                          Current project details and team access.
                        </CardDescription>
                      </div>
                      {canManageProjectDetails ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsProjectDetailsDialogOpen(true)}
                        >
                          <PencilLine className="size-4" />
                          Edit Details
                        </Button>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {project.projectAddress?.trim() ? null : (
                      <p className="max-w-3xl text-sm text-muted-foreground">
                        Add a project address to give the team clearer jobsite context.
                      </p>
                    )}

                    <div className="space-y-2">
                      <div className="grid gap-x-8 gap-y-3 lg:grid-cols-3">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                          Project details
                        </h3>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                          Leadership
                        </h3>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                          Activity
                        </h3>
                      </div>
                      <dl className="grid gap-x-8 border-b lg:grid-cols-3">
                        <ProjectOverviewField label="Project number" value={project.projectNumber} />
                        <ProjectOverviewField
                          label="Project manager"
                          value={projectManager?.name}
                          detail={projectManager?.email}
                        />
                        <ProjectOverviewField label="Created" value={formatDate(project.createdAt)} />
                      </dl>
                      <dl className="grid gap-x-8 lg:grid-cols-3">
                        <ProjectOverviewField label="Project address" value={project.projectAddress} />
                        <ProjectOverviewField
                          label="Project team"
                          value={`${memberList.length} member${memberList.length === 1 ? "" : "s"}`}
                        />
                        <ProjectOverviewField
                          label="Last updated"
                          value={project.updatedAt ? formatDate(project.updatedAt) : null}
                        />
                      </dl>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle>Project Documents</CardTitle>
                        <CardDescription>
                          All project documents, grouped by folder in the table.
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {canEditWork && (
                          <ProjectDocumentAddMenu
                            isUploading={isBulkUploading}
                            onCreateFolder={handleOverviewCreateFolder}
                            onUploadDocuments={handleOverviewBulkUploadClick}
                          />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => appNavigate({ to: "/project/documents", title: "Documents" })}
                        >
                          <FolderOpen className="size-4" />
                          View All
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ProjectDocumentsTable
                      documents={overviewDocuments}
                      showFolderColumn
                      compact
                      emptyMessage="No project documents yet."
                      onOpenDocument={(documentId) => openDocumentPreview(documentId)}
                      onUploadRevision={canEditWork ? handleRevisionUploadClick : undefined}
                      getRevisionRetry={getRevisionRetry}
                      onDownloadDocument={(document) => {
                        void handleDownloadDocument(document);
                      }}
                      onMoveDocument={
                        canEditWork
                          ? (document) => handleOpenMoveDialog([document._id], document.folderPath)
                          : undefined
                      }
                      onEditScheduleTables={(document) =>
                        appNavigate({
                          to: "/project/documents/$documentId/scheduleTables",
                          params: { documentId: String(document._id) },
                          search: document.latestRevision?._id
                            ? { revisionId: String(document.latestRevision._id) }
                            : undefined,
                          title: canEditWork ? "Edit Schedule Tables" : "Schedule Tables",
                        })
                      }
                      isUploadingRevision={isUploadingRevision}
                      revisionUploadDocumentId={revisionUploadDocumentId}
                      downloadingDocumentId={
                        downloadingRevisionId
                          ? overviewDocuments.find(
                              (document) =>
                                revisionRowsByDocumentId.get(String(document._id))?.latestRevision
                                  ?._id === downloadingRevisionId
                            )?._id ?? null
                          : null
                      }
                    />
                    {alphabetizedDocuments.length > overviewDocuments.length ? (
                      <div className="mt-2 text-right text-xs text-muted-foreground">
                        Showing {overviewDocuments.length} of {alphabetizedDocuments.length}. View all documents for the full library.
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                <Card className="self-start">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle>Project team</CardTitle>
                        <CardDescription>People who currently have access to this project.</CardDescription>
                      </div>
                      {canManageProjectDetails ? (
                        <IconButton
                          label="Manage project access"
                          icon={Plus}
                          variant="default"
                          onClick={() =>
                            setAccessProject({
                              _id: typedProjectId,
                              name: project.name,
                              projectManagerUserId: project.projectManagerUserId,
                            })
                          }
                        />
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isMembersPending ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" />
                        Loading members...
                      </div>
                    ) : isMembersError ? (
                      <p className="text-sm text-destructive">
                        {membersError?.message || "Unable to load members right now."}
                      </p>
                    ) : memberList.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No project members found.</p>
                    ) : (
                      <div className="space-y-2">
                        {memberList.map((member) => (
                          <div
                            key={member._id}
                            className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">{member.name?.trim() || member.email}</p>
                              <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                            </div>
                            <RoleBadge role={member.role} />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Project Stats</CardTitle>
                    <CardDescription>Work completed by this project team.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <ProjectStat
                      label="Documents"
                      description="Project library files"
                      value={documents.length}
                      onClick={() => appNavigate({ to: "/project/documents", title: "Documents" })}
                    />
                    <ProjectStat
                      label="SpecPilot"
                      description="Submittal jobs"
                      value={specPilotJobs?.length}
                      onClick={() => appNavigate({ to: "/specPilot", title: "SpecPilot" })}
                    />
                    <ProjectStat
                      label="TakeoffPilot"
                      description="Takeoff jobs"
                      value={projectTakeoffPilotJobCount}
                      onClick={() => appNavigate({ to: "/takeoffPilot", title: "TakeoffPilot" })}
                    />
                    <ProjectStat
                      label="RevPilot"
                      description="Revision review jobs"
                      value={revPilotJobs?.length}
                      onClick={() => appNavigate({ to: "/revPilot", title: "RevPilot" })}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <ActionLogCard
              sessions={actionLogSessions as ActionLogSession[] | undefined}
              isPending={isActionLogPending}
              isError={isActionLogError}
            />

            <Dialog open={isProjectDetailsDialogOpen} onOpenChange={setIsProjectDetailsDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Project Details</DialogTitle>
                  <DialogDescription>
                    These fields populate project overview cards and generated report headers.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleProjectDetailsSubmit(handleSaveProjectDetails)}>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <label htmlFor="project-name-edit" className="text-sm font-medium">
                        Project Name
                      </label>
                      <Input
                        id="project-name-edit"
                        {...registerProjectDetails("projectName")}
                        disabled={isSavingProjectDetails}
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label htmlFor="project-number-edit" className="text-sm font-medium">
                          Project Number
                        </label>
                        <Input
                          id="project-number-edit"
                          {...registerProjectDetails("projectNumber")}
                          disabled={isSavingProjectDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="project-manager-edit" className="text-sm font-medium">
                          Project Manager
                        </label>
                        <Controller
                          control={projectDetailsControl}
                          name="projectManager"
                          render={({ field }) => (
                            <Select
                              value={field.value || NO_PROJECT_MANAGER_VALUE}
                              onValueChange={field.onChange}
                              disabled={isSavingProjectDetails}
                            >
                              <SelectTrigger id="project-manager-edit">
                                <SelectValue placeholder="Select project manager" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NO_PROJECT_MANAGER_VALUE}>No project manager</SelectItem>
                                {organizationMembers.map((member) => (
                                  <SelectItem key={member._id} value={member._id}>
                                    {member.name} ({member.email})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="project-address-edit" className="text-sm font-medium">
                        Project Address
                      </label>
                      <Input
                        id="project-address-edit"
                        {...registerProjectDetails("projectAddress")}
                        disabled={isSavingProjectDetails}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsProjectDetailsDialogOpen(false)}
                      disabled={isSavingProjectDetails}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSavingProjectDetails}>
                      {isSavingProjectDetails ? <Loader2 className="size-4 animate-spin" /> : null}
                      Save Details
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

          </TabsContent>
          )}

          <TabsContent value="documents" className="space-y-4">
            <div
              className={cn(
                "space-y-4 rounded-xl transition-colors",
                isFileDropActive && activeDropFolderPath === null
                  ? "bg-primary/5 ring-2 ring-primary/40 ring-offset-2 ring-offset-background"
                  : null
              )}
              onDragOver={canEditWork ? handleLibraryDragOver : undefined}
              onDragLeave={canEditWork ? handleLibraryDragLeave : undefined}
              onDrop={canEditWork ? handleLibraryDrop : undefined}
            >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">Project document library</p>
                <p className="text-sm text-muted-foreground">
                  {canEditWork
                    ? "Drop files to upload, or drag documents onto a folder or breadcrumb to move them."
                    : "Open a document to view it, or download its latest revision."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {canEditWork && (
                  <ProjectDocumentAddMenu
                    isUploading={isBulkUploading}
                    onCreateFolder={() => setIsCreateFolderDialogOpen(true)}
                    onUploadDocuments={handleBulkUploadClick}
                    onUploadRevision={handleOpenRevisionUploadDialog}
                  />
                )}
                {canEditWork && selectedVisibleDocumentIds.length > 0 ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (selectedVisibleDocumentIds.length === 0) {
                          return;
                        }
                        handleOpenMoveDialog(selectedVisibleDocumentIds, currentFolderPath);
                      }}
                    >
                      <FolderOpen className="size-4" />
                      {`Move Selected (${selectedVisibleDocumentIds.length})`}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (selectedVisibleDocumentIds.length === 0) {
                          return;
                        }
                        setBulkDeleteDocumentIds(selectedVisibleDocumentIds);
                      }}
                    >
                      <Trash2 />
                      {`Delete Selected (${selectedVisibleDocumentIds.length})`}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1 text-sm">
              {folderTrail.map((folder, index) => (
                <div key={folder.path || "root"} className="flex items-center gap-1">
                  {index > 0 ? <ChevronRight className="size-4 text-muted-foreground" /> : null}
                  <Button
                    type="button"
                    variant={
                      activeDropFolderPath === folder.path
                        ? "default"
                        : folder.path === currentFolderPath
                          ? "secondary"
                          : "ghost"
                    }
                    size="xs"
                    aria-current={folder.path === currentFolderPath ? "location" : undefined}
                    onClick={() => setCurrentFolderPath(folder.path)}
                    {...(canEditWork && {
                      onDragOver: (event: DragEvent) => handleFolderDragOver(event, folder.path),
                      onDragLeave: (event: DragEvent) => handleFolderDragLeave(event, folder.path),
                      onDrop: (event: DragEvent) => handleFolderDrop(event, folder.path),
                    })}
                  >
                    {folder.label}
                  </Button>
                </div>
              ))}
            </div>

            {visibleFolders.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {visibleFolders.map((folder) => (
                  <button
                    key={folder._id}
                    type="button"
                    onClick={() => setCurrentFolderPath(folder.path)}
                    {...(canEditWork && {
                      onDragOver: (event: DragEvent) => handleFolderDragOver(event, folder.path),
                      onDragLeave: (event: DragEvent) => handleFolderDragLeave(event, folder.path),
                      onDrop: (event: DragEvent) => handleFolderDrop(event, folder.path),
                    })}
                    className={cn(
                      "flex min-w-[220px] items-center gap-3 rounded-xl border-2 bg-muted/20 px-4 py-3 text-left transition-colors hover:border-primary hover:bg-muted/40",
                      activeDropFolderPath === folder.path && "border-primary bg-primary/10"
                    )}
                  >
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-background text-primary shadow-sm">
                      <Folder className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium">{folder.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {countDocumentsInFolder(allDocuments, folder.path)} document
                        {countDocumentsInFolder(allDocuments, folder.path) === 1 ? "" : "s"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}

            {visibleFolders.length === 0 && documentsInCurrentFolder.length === 0 ? (
              <Card
                className={cn(
                  isFileDropActive && activeDropFolderPath === null && "border-primary bg-primary/5"
                )}
              >
                <CardContent className="py-12 text-center text-sm text-muted-foreground">
                  {!canEditWork
                    ? currentFolderPath
                      ? "This folder is empty."
                      : "No documents or folders yet."
                    : currentFolderPath
                      ? "This folder is empty. Drop files here, upload docs, or create a subfolder."
                      : "No documents or folders yet. Drop files here or use + to create a folder or upload docs."}
                </CardContent>
              </Card>
            ) : (
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">
                        {canEditWork && (
                          <Checkbox
                            aria-label="Select all visible documents"
                            checked={
                              areAllVisibleDocumentsSelected
                                ? true
                                : hasSomeVisibleDocumentsSelected
                                  ? "indeterminate"
                                  : false
                            }
                            onCheckedChange={(checked) => {
                              handleToggleAllVisibleDocuments(checked === true);
                            }}
                          />
                        )}
                      </TableHead>
                      <TableHead>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="-ms-2 px-2 text-xs font-bold uppercase tracking-wider"
                          onClick={nameColumn?.getToggleSortingHandler()}
                          aria-label={`Sort documents by name ${
                            nameColumn?.getIsSorted() === "asc"
                              ? "descending"
                              : "ascending"
                          }`}
                        >
                          Name
                          {renderSortIcon(nameColumn?.getIsSorted() ?? false)}
                        </Button>
                      </TableHead>
                      <TableHead>Latest Revision</TableHead>
                      <TableHead className="w-[120px]">
                        <div className="flex items-center gap-2">
                          <span>Type</span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <IconButton
                                label={
                                  selectedDocumentTypeFilters.length > 0
                                    ? `Filter documents by type (${selectedDocumentTypeFilters.length} active)`
                                    : "Filter documents by type"
                                }
                                icon={Filter}
                                size="xs"
                                variant={selectedDocumentTypeFilters.length > 0 ? "secondary" : "ghost"}
                              />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44">
                              <DropdownMenuLabel>Filter by type</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {DOCUMENT_TYPE_FILTER_OPTIONS.map((option) => (
                                <DropdownMenuCheckboxItem
                                  key={option.value}
                                  checked={selectedDocumentTypeFilters.includes(option.value)}
                                  onCheckedChange={(checked) => {
                                    handleDocumentTypeFilterChange(option.value, checked === true);
                                  }}
                                  onSelect={(event) => event.preventDefault()}
                                >
                                  {option.label}
                                </DropdownMenuCheckboxItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableHead>
                      <TableHead className="w-[140px]">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="-ms-2 px-2 text-xs font-bold uppercase tracking-wider"
                          onClick={updatedColumn?.getToggleSortingHandler()}
                          aria-label={`Sort documents by updated date ${
                            updatedColumn?.getIsSorted() === "asc"
                              ? "descending"
                              : "ascending"
                          }`}
                        >
                          Updated
                          {renderSortIcon(updatedColumn?.getIsSorted() ?? false)}
                        </Button>
                      </TableHead>
                      <TableHead className="w-[60px]">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                          {documentsInCurrentFolder.length === 0
                            ? "No documents in this location yet."
                            : "No documents match this type filter."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleDocuments.flatMap((document) => {
                        const { latestRevision, historyRevisions } = revisionRowsByDocumentId.get(
                          String(document._id)
                        ) ?? { latestRevision: document.latestRevision ?? null, historyRevisions: [] };
                        const isRevisionHistoryOpen = openRevisionDocumentIds.has(String(document._id));
                        const latestRetry = latestRevision ? getRevisionRetry(latestRevision) : null;
                        const documentRow = (
                          <TableRow key={document._id} className="group">
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                {canEditWork && (
                                  <>
                                    <button
                                      type="button"
                                      draggable={!isMovingDocument && !isBulkUploading}
                                      onDragStart={(event) =>
                                        handleDocumentDragStart(event, document._id)
                                      }
                                      onDragEnd={handleDocumentDragEnd}
                                      className="flex h-8 w-6 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
                                      aria-label={`Drag ${document.name} to a folder`}
                                    >
                                      <GripVertical className="size-4" />
                                    </button>
                                    <Checkbox
                                      aria-label={`Select ${document.name}`}
                                      checked={selectedDocumentIds.has(String(document._id))}
                                      onCheckedChange={(checked) => {
                                        toggleDocumentSelection(document._id, checked === true);
                                      }}
                                    />
                                  </>
                                )}
                                {historyRevisions.length > 0 ? (
                                  <IconButton
                                    label={`${
                                      isRevisionHistoryOpen ? "Hide" : "Show"
                                    } revisions for ${document.name}`}
                                    icon={isRevisionHistoryOpen ? ChevronDown : ChevronRight}
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-expanded={isRevisionHistoryOpen}
                                    onClick={() => handleToggleRevisionHistory(document._id)}
                                  />
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="min-w-[280px]">
                              <ProjectDocumentNameCell
                                document={document}
                                startEditing={inlineRenameDocumentId === document._id}
                                onStartEditingHandled={() => setInlineRenameDocumentId(null)}
                                onOpen={(documentId) => openDocumentPreview(documentId)}
                                onUpdateName={handleUpdateDocumentName}
                              />
                            </TableCell>
                            <TableCell>
                              {(() => {
                                if (!latestRevision) {
                                  return <span className="text-sm text-muted-foreground">No revisions</span>;
                                }

                                return (
                                  <div className="flex min-w-0 flex-col items-start gap-1">
                                    <span className="flex max-w-full items-center gap-2">
                                      <RevisionBadge number={latestRevision.revisionNumber} />
                                      <span className="truncate text-sm font-medium">
                                        {latestRevision.revisionName}
                                      </span>
                                      <LinkedRevisionJobsButton
                                        jobs={getLinkedRevisionJobs(latestRevision)}
                                        compact
                                        hideWhenEmpty
                                      />
                                    </span>
                                      <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                                        {buildDocumentDownloadFilename(
                                          document.name,
                                          latestRevision.originalFilename,
                                          latestRevision.revisionNumber
                                        )}
                                      </span>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="flex flex-col items-start gap-1">
                                <DocumentTypeLabel
                                  {...revisionTypeLabelProps(latestRevision)}
                                  compact
                                />
                                {latestRevision ? renderSheetIndexToggle(latestRevision) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(document.updatedAt)}
                            </TableCell>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <DocumentActionsMenu
                                document={document}
                                latestRevision={latestRevision}
                                onOpenChange={(open) => {
                                  setDeleteEligibilityRequest(
                                    open
                                      ? {
                                          documentId: document._id,
                                          revisionId: latestRevision?._id,
                                        }
                                      : null
                                  );
                                }}
                                onRenameDocument={
                                  canEditWork ? () => setInlineRenameDocumentId(document._id) : undefined
                                }
                                onUploadRevision={
                                  canEditWork ? () => handleRevisionUploadClick(document._id) : undefined
                                }
                                isUploadingRevision={
                                  isUploadingRevision && revisionUploadDocumentId === document._id
                                }
                                onDownloadDocument={
                                  latestRevision
                                    ? () =>
                                        void handleDownloadRevision({
                                          documentName: document.name,
                                          revisionId: latestRevision._id,
                                          revisionNumber: latestRevision.revisionNumber,
                                          originalFilename: latestRevision.originalFilename,
                                        })
                                    : undefined
                                }
                                isDownloading={
                                  Boolean(latestRevision) && downloadingRevisionId === latestRevision?._id
                                }
                                isDownloadBusy={Boolean(downloadingRevisionId)}
                                onEditScheduleTables={
                                  latestRevision
                                    ? () =>
                                        appNavigate({
                                          to: "/project/documents/$documentId/scheduleTables",
                                          params: { documentId: String(document._id) },
                                          search: { revisionId: String(latestRevision._id) },
                                          title: canEditWork ? "Edit Schedule Tables" : "Schedule Tables",
                                        })
                                    : undefined
                                }
                                retry={latestRetry}
                                revisionActions={
                                  canEditWork && latestRevision ? (
                                    <RevisionDeleteDropdownItem
                                      revision={applyRevisionDeleteEligibility(latestRevision)}
                                      revisionCount={document.revisionCount}
                                      label="Delete latest revision"
                                      isDeleting={isDeletingRevision && deleteRevisionId === latestRevision._id}
                                      onRequestDelete={setDeleteRevisionId}
                                    />
                                  ) : null
                                }
                                onMoveDocument={
                                  canEditWork
                                    ? () => handleOpenMoveDialog([document._id], document.folderPath)
                                    : undefined
                                }
                                destructiveActions={
                                  canEditWork ? (
                                    <DocumentDeleteDropdownItem
                                      document={applyDocumentDeleteEligibility(document)}
                                      isDeleting={isDeletingDocument && deleteDocumentId === document._id}
                                      onRequestDelete={setDeleteDocumentId}
                                    />
                                  ) : null
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );

                        const latestSheetIndexRow = latestRevision
                          ? renderSheetIndexRow(document._id, latestRevision)
                          : null;
                        if (!isRevisionHistoryOpen) {
                          return [documentRow, latestSheetIndexRow];
                        }

                        return [
                          documentRow,
                          latestSheetIndexRow,
                          ...historyRevisions.flatMap((revision) => {
                            const retry = getRevisionRetry(revision);
                            return [(
                            <DocumentSubRow
                              key={revision._id}
                              role="button"
                              tabIndex={0}
                              className="cursor-pointer bg-muted/15 transition hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              onClick={() => {
                                openDocumentPreview(document._id, revision._id);
                              }}
                              onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
                                // Keys from the row's own buttons (e.g. the Sheets toggle) must not open the preview.
                                if (
                                  event.target === event.currentTarget &&
                                  (event.key === "Enter" || event.key === " ")
                                ) {
                                  event.preventDefault();
                                  openDocumentPreview(document._id, revision._id);
                                }
                              }}
                              aria-label={`Open ${document.name} revision ${revision.revisionName}`}
                              name={<span className="text-sm text-muted-foreground">Previous revision</span>}
                              revision={
                                <div className="flex min-w-0 flex-col items-start gap-1">
                                  <span className="flex max-w-full items-center gap-2">
                                    <RevisionBadge number={revision.revisionNumber} />
                                    <span className="truncate text-sm font-medium">
                                      {revision.revisionName}
                                    </span>
                                    <LinkedRevisionJobsButton
                                      jobs={getLinkedRevisionJobs(revision)}
                                      compact
                                      hideWhenEmpty
                                    />
                                  </span>
                                  <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                                    {buildDocumentDownloadFilename(
                                      document.name,
                                      revision.originalFilename,
                                      revision.revisionNumber
                                    )}
                                  </span>
                                </div>
                              }
                              type={
                                <div className="flex flex-col items-start gap-1 text-sm">
                                  <DocumentTypeLabel
                                    {...revisionTypeLabelProps(revision)}
                                    compact
                                  />
                                  {renderSheetIndexToggle(revision)}
                                </div>
                              }
                              updated={formatDate(revision._creationTime)}
                              actions={
                                <RowActionsMenu
                                  label={`Actions for revision ${revision.revisionName}`}
                                  onOpenChange={(open) => {
                                    setDeleteEligibilityRequest(
                                      open
                                        ? {
                                            documentId: document._id,
                                            revisionId: revision._id,
                                          }
                                        : null
                                    );
                                  }}
                                >
                                    <RowAction
                                      icon={downloadingRevisionId === revision._id ? SpinnerIcon : Download}
                                      disabled={Boolean(downloadingRevisionId)}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        void handleDownloadRevision({
                                          documentName: document.name,
                                          revisionId: revision._id,
                                          revisionNumber: revision.revisionNumber,
                                          originalFilename: revision.originalFilename,
                                        });
                                      }}
                                    >
                                      Download
                                    </RowAction>
                                    {retry ? <RetryProcessingAction {...retry} /> : null}
                                    {canEditWork && (
                                      <RevisionDeleteDropdownItem
                                        revision={applyRevisionDeleteEligibility(revision)}
                                        revisionCount={document.revisionCount}
                                        label="Delete revision"
                                        isDeleting={isDeletingRevision && deleteRevisionId === revision._id}
                                        onRequestDelete={setDeleteRevisionId}
                                      />
                                    )}
                                </RowActionsMenu>
                              }
                            />
                          ), renderSheetIndexRow(document._id, revision)];
                          }),
                        ];
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog
          open={Boolean(selectedDocumentId)}
          onOpenChange={(open) => {
            if (!open) closeDocumentPreview();
          }}
        >
          <DialogContent className="flex h-[85dvh] max-h-[85dvh] w-[85vw] max-w-[85vw] flex-col overflow-hidden p-0 sm:max-w-[85vw]">
            <DialogHeader className="sr-only">
              <DialogTitle>{selectedDocumentDetails?.document.name ?? "Document preview"}</DialogTitle>
            </DialogHeader>

            {selectedDocumentDetails === undefined ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : selectedDocumentDetails === null ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Unable to load this document.
              </div>
            ) : (
              <ProjectDocumentViewer
                documentId={selectedDocumentDetails.document._id}
                documentName={selectedDocumentDetails.document.name}
                revisions={selectedDocumentDetails.revisions}
                initialRevisionId={selectedDocumentRevisionId}
                initialPage={selectedDocumentPage}
                onDeletedDocument={closeDocumentPreview}
                showViewFullButton
                showOpenRevCollabButton
                onViewFull={(revisionId) => handleOpenFullView(undefined, revisionId)}
                onOpenRevCollab={handleOpenRevCollab}
                previewOnly
                className="min-h-0 flex-1"
              />
            )}
          </DialogContent>
        </Dialog>

        {accessProject ? (
          <ProjectAccessDialog
            open={Boolean(accessProject)}
            onOpenChange={(open) => {
              if (!open) setAccessProject(null);
            }}
            projectId={accessProject._id}
            projectName={accessProject.name}
            lockedUserIds={
              accessProject.projectManagerUserId
                ? [accessProject.projectManagerUserId]
                : []
            }
            lockedUserLabel="project manager"
          />
        ) : null}
      </div>

      <Dialog
        open={isCreateFolderDialogOpen}
        onOpenChange={(open) => {
          setIsCreateFolderDialogOpen(open);
          if (!open) {
            setNewFolderName("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create folder</DialogTitle>
            <DialogDescription>
              {currentFolderPath
                ? `Create a new folder inside ${folderTrail.at(-1)?.label ?? "this folder"}.`
                : "Create a new top-level folder in the project library."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              aria-label="New folder name"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleCreateFolder();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleCreateFolder();
              }}
              disabled={isCreatingFolder || !newFolderName.trim()}
            >
              {isCreatingFolder ? <Loader2 className="size-4 animate-spin" /> : null}
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={moveDocumentIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setMoveDocumentIds([]);
            setMoveDestinationPath(ROOT_FOLDER_VALUE);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{moveTargetDocuments.length > 1 ? "Move documents" : "Move document"}</DialogTitle>
            <DialogDescription>
              {moveTargetDocuments.length === 1
                ? `Move ${moveTargetDocuments[0]?.name ?? "this document"} to a different folder.`
                : moveTargetDocuments.length > 1
                  ? `Move ${moveTargetDocuments.length} documents to a different folder.`
                  : "Choose where these documents should live."}
            </DialogDescription>
          </DialogHeader>
          <Select value={moveDestinationPath} onValueChange={setMoveDestinationPath}>
            <SelectTrigger>
              <SelectValue placeholder="Select destination" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ROOT_FOLDER_VALUE}>Project Documents</SelectItem>
              {allFolders.map((folder) => (
                <SelectItem key={folder._id} value={folder.path}>
                  <div className="flex flex-col items-start gap-0.5">
                    <span>{`${formatFolderOptionPrefix(folder.path.split("/").length - 1)}${folder.name}`}</span>
                    {folder.path.includes("/") ? (
                      <span className="text-xs text-muted-foreground">{folder.path}</span>
                    ) : null}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMoveDocumentIds([]);
                setMoveDestinationPath(ROOT_FOLDER_VALUE);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleMoveDocument();
              }}
              disabled={
                isMovingDocument ||
                moveTargetDocuments.length === 0 ||
                moveTargetDocuments.every(
                  (document) => (document.folderPath || ROOT_FOLDER_VALUE) === moveDestinationPath
                )
              }
            >
              {isMovingDocument ? <Loader2 className="size-4 animate-spin" /> : null}
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkDeleteDocumentIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            if (isBulkDeletingDocuments) {
              return;
            }
            setBulkDeleteDocumentIds([]);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-lg"
          showCloseButton={!isBulkDeletingDocuments}
        >
          <DialogHeader>
            <DialogTitle>
              {isBulkDeleteEligibilityError
                ? "Couldn't check delete permissions"
                : blockedBulkDeleteDocuments.length > 0
                  ? "Cannot delete selected documents"
                  : bulkDeleteDocumentIds.length > 1
                    ? "Delete documents?"
                    : "Delete document?"}
            </DialogTitle>
            <DialogDescription>
              {isBulkDeleteEligibilityError
                ? "We couldn't verify whether the selected documents can be deleted. Close this dialog and try again."
                : isCheckingBulkDeleteEligibility
                  ? "Checking whether the selected documents can be deleted."
                  : blockedBulkDeleteDocuments.length > 0
                    ? "At least one selected document cannot be deleted. Remove the blocked documents from the selection before deleting."
                    : bulkDeleteTargetDocuments.length > 1
                      ? `Delete ${bulkDeleteTargetDocuments.length} documents and all of their revisions? This cannot be undone.`
                      : `Delete "${bulkDeleteTargetDocuments[0]?.name ?? "this document"}" and all of its revisions? This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>

          {isBulkDeleteEligibilityError ? (
            <div className="rounded-md border border-destructive/50 px-3 py-2 text-sm text-destructive">
              Failed to load delete eligibility. Please close and try again.
            </div>
          ) : isCheckingBulkDeleteEligibility ? (
            <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Checking delete permissions...
            </div>
          ) : blockedBulkDeleteDocuments.length > 0 ? (
            <div className="max-h-64 overflow-auto rounded-md border">
              {blockedBulkDeleteDocuments.map((document) => (
                <div
                  key={document.documentId}
                  className="border-b px-3 py-2 last:border-b-0"
                >
                  <p className="truncate text-sm font-medium text-foreground">
                    {document.name}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatRevisionDeleteBlockedReason(document.deleteBlockedReason) ??
                      JOB_ATTACHED_DELETE_REASON}
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkDeleteDocumentIds([]);
              }}
              disabled={isBulkDeletingDocuments}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void handleBulkDeleteDocuments();
              }}
              disabled={
                isBulkDeletingDocuments ||
                isCheckingBulkDeleteEligibility ||
                bulkDeleteEligibility?.canDeleteAll !== true
              }
            >
              {isBulkDeletingDocuments ? <Loader2 className="size-4 animate-spin" /> : null}
              {isBulkDeletingDocuments ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(deleteDocumentId)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDocumentId(null);
          }
        }}
        title="Delete document?"
        description={`Delete "${deleteTargetDocument?.name ?? "this document"}" and all of its revisions? This cannot be undone.`}
        onConfirm={() => {
          void handleDeleteDocument(deleteDocumentId);
        }}
        disabled={isDeletingDocument}
        confirmLabel={isDeletingDocument ? "Deleting..." : "Delete"}
        confirmVariant="destructive"
      />

      <ConfirmationDialog
        open={Boolean(deleteRevisionId)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteRevisionId(null);
          }
        }}
        title="Delete revision?"
        description={`Delete "${deleteTargetRevision?.revisionName ?? "this revision"}" and its derived data? This cannot be undone.`}
        onConfirm={() => {
          void handleDeleteRevision(deleteRevisionId);
        }}
        disabled={isDeletingRevision}
        confirmLabel={isDeletingRevision ? "Deleting..." : "Delete"}
        confirmVariant="destructive"
      />
    </>
  );
}
